import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import type { AssessmentQuestion, ReadingStepComponentProps } from "./types";
import { buildPromptSegments } from "../utils";
import { theme } from "../../../../theme/theme";
import { shuffle } from "../../../../lib/arrayOperations";
import { highlightTextSx, spacing } from "../../../../theme/styles";
import { InstructionAudioButton } from "../../../../components/InstructionAudioButton";

export type ResolvedVocabularyPromptSegment =
  | { type: "text"; value: string }
  | { type: "placeholder"; raw: string; questionId: string; value: string };

type VocabularyQuestionItem = AssessmentQuestion & {
  shuffled_options: string[];
};

const getVocabularyPlaceholderId = (question: AssessmentQuestion): string =>
  question.placeholder_id?.trim() || question.question_id;

const boundaryPunctuationPattern = /^[\s\p{P}]+|[\s\p{P}]+$/gu;

export const normalizeVocabularyWord = (value: string): string =>
  value.replace(boundaryPunctuationPattern, "");

export const replaceSegmentWord = (
  originalValue: string,
  originalWord: string | undefined,
  selectedWord: string,
): string => {
  const normalizedSelectedWord = normalizeVocabularyWord(selectedWord);
  if (!normalizedSelectedWord) {
    return originalValue;
  }

  const normalizedOriginalWord = normalizeVocabularyWord(originalWord ?? "");
  if (!normalizedOriginalWord) {
    return normalizedSelectedWord;
  }

  const escapedOriginalWord = normalizedOriginalWord.replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&",
  );

  const wordPattern = new RegExp(escapedOriginalWord, "i");
  const match = wordPattern.exec(originalValue);
  if (match) {
    return `${originalValue.slice(
      0,
      match.index,
    )}${normalizedSelectedWord}${originalValue.slice(
      match.index + match[0].length,
    )}`;
  }

  return normalizedSelectedWord;
};

export const resolveVocabularyPromptSegments = (
  maskedText: string,
  promptText: string,
): ResolvedVocabularyPromptSegment[] => {
  const promptSegments = buildPromptSegments(maskedText);
  const resolvedSegments: ResolvedVocabularyPromptSegment[] = [];
  let promptCursor = 0;

  for (const [index, segment] of promptSegments.entries()) {
    if (segment.type === "text") {
      resolvedSegments.push(segment);
      if (segment.value) {
        const textIndex = promptText.indexOf(segment.value, promptCursor);
        if (textIndex >= 0) {
          promptCursor = textIndex + segment.value.length;
        }
      }
      continue;
    }

    const nextTextSegment = promptSegments
      .slice(index + 1)
      .find((nextSegment) => nextSegment.type === "text" && nextSegment.value);
    const nextTextIndex =
      nextTextSegment?.type === "text"
        ? promptText.indexOf(nextTextSegment.value, promptCursor)
        : -1;
    const replacement =
      nextTextIndex >= 0
        ? promptText.slice(promptCursor, nextTextIndex)
        : promptText.slice(promptCursor);

    resolvedSegments.push({
      type: "placeholder",
      raw: segment.raw,
      questionId: segment.questionId,
      value: replacement || segment.raw,
    });
    promptCursor = nextTextIndex >= 0 ? nextTextIndex : promptText.length;
  }

  return resolvedSegments;
};

export function VocabularyQuestion({
  assessment,
  isLoading,
  onStepReadyChange,
  onSaveAnswer,
}: ReadingStepComponentProps) {
  const vocabQuestions = useMemo<VocabularyQuestionItem[]>(() => {
    const partQuestions = assessment?.part?.questions ?? [];
    const maskedPrompt = assessment?.part?.vocabMaskedPrompt ?? "";
    const placeholderPattern = /\{\{\s*(.+?)\s*}}/g;
    const firstPlaceholderPositionById: Record<string, number> = {};

    for (
      let match = placeholderPattern.exec(maskedPrompt);
      match;
      match = placeholderPattern.exec(maskedPrompt)
    ) {
      const questionId = (match[1] ?? "").trim();
      if (!questionId) {
        continue;
      }
      if (firstPlaceholderPositionById[questionId] === undefined) {
        firstPlaceholderPositionById[questionId] = match.index;
      }
    }

    return partQuestions
      .filter((question) => {
        const type = (question.question_type ?? "").toLowerCase().trim();
        return type === "vocabulary" || type === "vocab";
      })
      .sort((a, b) => {
        const aPos =
          firstPlaceholderPositionById[getVocabularyPlaceholderId(a)];
        const bPos =
          firstPlaceholderPositionById[getVocabularyPlaceholderId(b)];

        const aHasPos = aPos !== undefined;
        const bHasPos = bPos !== undefined;
        if (aHasPos && bHasPos && aPos !== bPos) {
          return aPos - bPos;
        }
        if (aHasPos && !bHasPos) {
          return -1;
        }
        if (!aHasPos && bHasPos) {
          return 1;
        }
        return a.question_order - b.question_order;
      })
      .map((question) => ({
        ...question,
        shuffled_options: shuffle([...(question.options ?? [])]),
      }));
  }, [assessment?.part?.questions, assessment?.part?.vocabMaskedPrompt]);

  const initialAnswerByQuestionId = useMemo(
    () =>
      Object.fromEntries(
        vocabQuestions
          .map((question) => [
            question.question_id,
            assessment?.submissionProgress?.answers_by_question_id?.[
              question.question_id
            ] ?? "",
          ])
          .filter(([, answer]) => Boolean(answer)),
      ),
    [assessment?.submissionProgress?.answers_by_question_id, vocabQuestions],
  );
  const initialActiveQuestionIndex = useMemo(() => {
    const firstUnansweredIndex = vocabQuestions.findIndex(
      (question) =>
        !assessment?.submissionProgress?.answers_by_question_id?.[
          question.question_id
        ],
    );
    return firstUnansweredIndex >= 0 ? firstUnansweredIndex : 0;
  }, [assessment?.submissionProgress?.answers_by_question_id, vocabQuestions]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(
    initialActiveQuestionIndex,
  );
  const [answerByQuestionId, setAnswerByQuestionId] = useState<
    Record<string, string>
  >(() => initialAnswerByQuestionId);
  const safeQuestionIndex = Math.min(
    activeQuestionIndex,
    Math.max(vocabQuestions.length - 1, 0),
  );
  const activeQuestion = vocabQuestions[safeQuestionIndex];
  const promptText = assessment?.part?.prompt ?? "";
  const vocabMaskedText = assessment?.part?.vocabMaskedPrompt ?? "";
  const resolvedPromptSegments = useMemo(
    () => resolveVocabularyPromptSegments(vocabMaskedText, promptText),
    [promptText, vocabMaskedText],
  );
  const questionByPlaceholderId = useMemo(() => {
    const questions = new Map<string, VocabularyQuestionItem>();
    for (const question of vocabQuestions) {
      questions.set(question.question_id, question);
      questions.set(getVocabularyPlaceholderId(question), question);
    }
    return questions;
  }, [vocabQuestions]);
  const handleAnswerChange = (questionId: string, nextValue: string) => {
    setAnswerByQuestionId((current) => {
      if (!nextValue) {
        return Object.fromEntries(
          Object.entries(current).filter(([key]) => key !== questionId),
        );
      }

      return {
        ...current,
        [questionId]: nextValue,
      };
    });

    void onSaveAnswer?.(questionId, nextValue);
  };
  const isFirstQuestion = safeQuestionIndex === 0;
  const isLastQuestion = safeQuestionIndex >= vocabQuestions.length - 1;
  const currentQuestionIsAnswerable =
    (activeQuestion?.shuffled_options ?? []).length > 0;
  const currentQuestionAnswered = Boolean(
    activeQuestion && answerByQuestionId[activeQuestion.question_id],
  );
  const answerableVocabularyQuestions = useMemo(
    () =>
      vocabQuestions.filter(
        (question) => (question.shuffled_options ?? []).length > 0,
      ),
    [vocabQuestions],
  );
  const hasVocabularyContent =
    Boolean(promptText.trim() || vocabMaskedText.trim()) &&
    answerableVocabularyQuestions.length > 0;
  const allAnswered =
    answerableVocabularyQuestions.length > 0 &&
    answerableVocabularyQuestions.every((question) =>
      Boolean(answerByQuestionId[question.question_id]),
    );
  const isVocabularyComplete =
    !hasVocabularyContent ||
    (allAnswered &&
      isLastQuestion &&
      (currentQuestionAnswered || !currentQuestionIsAnswerable));

  useEffect(() => {
    onStepReadyChange?.(isVocabularyComplete);
  }, [isVocabularyComplete, onStepReadyChange]);

  if (isLoading) {
    return null;
  }

  if (!assessment?.part) {
    return <Typography>No part content available.</Typography>;
  }

  if (!hasVocabularyContent) {
    return (
      <Typography>
        Vocabulary content is unavailable for this part. You can continue to the
        next step.
      </Typography>
    );
  }

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={3}
      sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}
    >
      <Card
        data-testid="vocabulary-prompt-panel"
        sx={{
          flex: "60%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Typography variant={"body1"}>
            {resolvedPromptSegments.map((segment, index) => {
              if (segment.type === "text") {
                return <span key={`text-${index}`}>{segment.value}</span>;
              }

              const isActiveQuestionWord =
                Boolean(activeQuestion) &&
                questionByPlaceholderId.get(segment.questionId)?.question_id ===
                  activeQuestion?.question_id;
              const segmentQuestion = questionByPlaceholderId.get(
                segment.questionId,
              );

              return (
                <Box
                  key={`placeholder-${segment.questionId}-${index}`}
                  component={"span"}
                  sx={(theme) => ({
                    ...(isActiveQuestionWord
                      ? {
                          padding: "1px 6px",
                          ...highlightTextSx(theme, false),
                          cursor: "default",
                          display: "inline-block",
                        }
                      : {}),
                  })}
                >
                  {replaceSegmentWord(
                    segment.value,
                    segmentQuestion?.highlightedText,
                    segmentQuestion
                      ? (answerByQuestionId[segmentQuestion.question_id] ?? "")
                      : "",
                  )}
                </Box>
              );
            })}
          </Typography>
        </CardContent>
      </Card>
      <Card
        data-testid="vocabulary-question-panel"
        sx={{
          flex: "40%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
        variant={"filled"}
      >
        <CardContent
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: 2,
          }}
        >
          {activeQuestion ? (
            <>
              <Stack spacing={1} sx={{ display: "flex" }}>
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <InstructionAudioButton
                    audioUrl={assessment?.vocabQuestionInstructionAudio}
                    label="Play vocabulary question instruction audio"
                  />
                  <Typography variant={"body1"} sx={{ fontWeight: 800 }}>
                    Instructions:
                  </Typography>
                </Stack>
                <Stack>
                  <Typography sx={{ fontWeight: 600 }}>
                    Which word is most similar in meaning to{" "}
                    <Box
                      component="span"
                      sx={{
                        textTransform: "uppercase",
                        fontWeight: 700,
                      }}
                    >
                      {activeQuestion.highlightedText}
                    </Box>
                    ?
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Question {safeQuestionIndex + 1} of {vocabQuestions.length}
                  </Typography>
                </Stack>

                <RadioGroup
                  value={answerByQuestionId[activeQuestion.question_id] ?? ""}
                  onChange={(event) =>
                    handleAnswerChange(
                      activeQuestion.question_id,
                      event.target.value,
                    )
                  }
                  sx={{ gap: "8px" }}
                >
                  {activeQuestion.shuffled_options.map((option, index) => (
                    <FormControlLabel
                      key={`${activeQuestion.question_id}-${index}`}
                      value={option}
                      control={<Radio />}
                      label={option}
                      onClick={(event) => {
                        if (
                          answerByQuestionId[activeQuestion.question_id] !==
                          option
                        ) {
                          return;
                        }

                        event.preventDefault();
                        handleAnswerChange(activeQuestion.question_id, "");
                      }}
                      sx={{
                        ml: 1,
                        backgroundColor: theme.palette.background.default,
                        border: `2px solid ${theme.palette.divider}`,
                        borderRadius: spacing.radius.xs,
                      }}
                    />
                  ))}
                </RadioGroup>
              </Stack>
              <Box>
                <Stack
                  direction="row"
                  spacing={1.5}
                  justifyContent={"space-between"}
                >
                  <Button
                    variant="outlined"
                    disabled={isFirstQuestion}
                    onClick={() =>
                      setActiveQuestionIndex((previous) =>
                        Math.max(previous - 1, 0),
                      )
                    }
                    size="medium"
                  >
                    Previous
                  </Button>
                  {!isVocabularyComplete ? (
                    <Button
                      variant="contained"
                      disabled={
                        isLastQuestion ||
                        (currentQuestionIsAnswerable &&
                          !currentQuestionAnswered)
                      }
                      onClick={() =>
                        setActiveQuestionIndex((previous) =>
                          Math.min(previous + 1, vocabQuestions.length - 1),
                        )
                      }
                      size="medium"
                    >
                      Next
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            </>
          ) : (
            <Typography>
              No vocabulary questions found for this part.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
